import { Injectable } from '@angular/core';
import {
  FeatureStatus,
  NaturalId,
} from '../population-menu/population.service';
import { MapHttpService } from '../../../../mapbox/services/map-http.service';
import { MapboxGeoJSONFeature } from 'mapbox-gl';
import {
  formatChartRangesAndHoverNumber,
  getPercents,
  getPieChartLayout,
} from '../shared/chart-utility';
import { Data, Layout } from 'plotly.js-dist-min';
import { convertArea } from '@turf/helpers';
import area from '@turf/area';
import { JsonResponse } from '../../../../../shared/api/backend-config';
import { of } from 'rxjs';
import { getFRCLabels } from '../../../../../shared/util/FrcDistribution';

export const HOUSEHOLDS_BY_VEHICLES = 'HOUSEHOLDS_BY_VEHICLES';
export const TIME_TO_DEPARTURE_TO_WORK = 'TIME_TO_DEPARTURE_TO_WORK';
export const TRAVEL_TIME_TO_WORK = 'TRAVEL_TIME_TO_WORK';
export const TRANSPORTATION_TO_WORK = 'TRANSPORTATION_TO_WORK';

export interface CommuteAndMobilityJSON {
  HOUSEHOLDS_BY_VEHICLES: HouseholdsByVehicles[];
  TIME_TO_DEPARTURE_TO_WORK: TimeOfDepartureToWork[];
  TRAVEL_TIME_TO_WORK: TravelTimeToWork[];
  TRANSPORTATION_TO_WORK: WorkCommuteModes[];
}

export interface HouseholdsByVehicles {
  year: number;
  vehiclesCount: number;
  households: number;
}

export interface WorkCommuteModes {
  year: number;
  travelType: string;
  workers: number;
}

export interface TravelTimeToWork {
  year: number;
  minutesFrom: number;
  minutesTo: number;
  workers: number;
}

export interface TimeOfDepartureToWork {
  year: number;
  timeInterval: string;
  workers: number;
}

export interface RoadLengthBackend {
  // length multiplied by lanes num
  roadLengthLanesWeighted: RoadLength;
  // summary length without considering lanes
  roadLength: RoadLength;
}

export interface RoadLength {
  motorway?: number;
  motorway_link?: number;
  primary?: number;
  primary_link?: number;
  trunk?: number;
  trunk_link?: number;
  residential?: number;
  secondary?: number;
  secondary_link?: number;
  service?: number;
  tertiary?: number;
  tertiary_link?: number;
}

export interface RoadDensityFeatureStatus extends FeatureStatus {
  containsRoadDensity: boolean;
}

export interface CommuteAndMobilityFeatureStatus extends FeatureStatus {
  containsHouseholdsByVehicles: boolean;
  householdsByVehiclesYear: number;
  containsWorkCommuteModes: boolean;
  workCommuteModesYear: number;
  containsTravelTimeToWork: boolean;
  travelTimeToWorkYear: number;
  containsTimeOfDepartureToWork: boolean;
  timeOfDepartureToWorkYear: number;
  containsRoadDensity: boolean;
  containsTrafficIntensity: boolean;
}

export interface TrafficIntensityBackend {
  distribution: RoadLength;
}

@Injectable({
  providedIn: 'root',
})
export class CommuteAndMobilityService {
  private householdsByVehiclesData: HouseholdsByVehicles[] = [];
  private timeOfDepartureToWorkData: TimeOfDepartureToWork[] = [];
  private travelTimeToWorkData: TravelTimeToWork[] = [];
  private workCommuteModesData: WorkCommuteModes[] = [];

  public readonly accessibleYears: number[] = [
    2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022,
  ];

  public featureStatus: CommuteAndMobilityFeatureStatus = {
    naturalId: '',
    isSelected: false,
    containsHouseholdsByVehicles: false,
    householdsByVehiclesYear: 2022,
    containsTimeOfDepartureToWork: false,
    timeOfDepartureToWorkYear: 2022,
    containsTravelTimeToWork: false,
    travelTimeToWorkYear: 2022,
    containsWorkCommuteModes: false,
    workCommuteModesYear: 2022,
    containsRoadDensity: false,
    containsTrafficIntensity: false,
  };

  public trafficIntensityChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public roadDensityChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public readonly householdSizeByVehiclesChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public readonly timeOfDepartureToWorkChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public readonly travelTimeToWorkChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public readonly workCommuteModesChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  constructor(private http: MapHttpService) {}

  public fillChartsData(feature: MapboxGeoJSONFeature): void {
    const id = feature.properties!.external_id;
    this.http
      .getDataForSingleCellUnified<CommuteAndMobilityJSON>(id, [
        HOUSEHOLDS_BY_VEHICLES,
      ])
      .subscribe((res) => {
        this.householdsByVehiclesData = res.object.HOUSEHOLDS_BY_VEHICLES;
        this.featureStatus.containsHouseholdsByVehicles = true;
        this.featureStatus.naturalId = id;
        this.featureStatus.isSelected = true;

        this.featureStatus.householdsByVehiclesYear = Math.max(
          ...this.householdsByVehiclesData.map((d) => d.year)
        );
        this.updateHouseholdsByVehiclesChartByYear(
          this.featureStatus.householdsByVehiclesYear!
        );
      });

    this.http
      .getDataForSingleCellUnified<CommuteAndMobilityJSON>(id, [
        TIME_TO_DEPARTURE_TO_WORK,
      ])
      .subscribe((res) => {
        this.timeOfDepartureToWorkData = res.object.TIME_TO_DEPARTURE_TO_WORK;
        this.featureStatus.containsTimeOfDepartureToWork = true;
        this.featureStatus.timeOfDepartureToWorkYear = Math.max(
          ...res.object.TIME_TO_DEPARTURE_TO_WORK.map((d) => d.year)
        );
        this.updateTimeOfDepartureToWorkChartByYear(
          this.featureStatus.timeOfDepartureToWorkYear!
        );
      });

    this.http
      .getDataForSingleCellUnified<CommuteAndMobilityJSON>(id, [
        TRAVEL_TIME_TO_WORK,
      ])
      .subscribe((res) => {
        this.travelTimeToWorkData = res.object.TRAVEL_TIME_TO_WORK;
        this.featureStatus.containsTravelTimeToWork = true;
        this.featureStatus.travelTimeToWorkYear = Math.max(
          ...res.object.TRAVEL_TIME_TO_WORK.map((d) => d.year)
        );
        this.updateTravelTimeToWorkChartByYear(
          this.featureStatus.travelTimeToWorkYear!
        );
      });

    this.http
      .getDataForSingleCellUnified<CommuteAndMobilityJSON>(id, [
        TRANSPORTATION_TO_WORK,
      ])
      .subscribe((res) => {
        this.workCommuteModesData = res.object.TRANSPORTATION_TO_WORK;
        this.featureStatus.containsWorkCommuteModes = true;
        this.featureStatus.workCommuteModesYear = Math.max(
          ...res.object.TRANSPORTATION_TO_WORK.map((d) => d.year)
        );
        this.updateWorkCommuteModesChartByYear(
          this.featureStatus.workCommuteModesYear!
        );
      });

    this.fillRoadDensityChart(feature);
    this.fillTrafficIntensityChart(feature);
  }

  public updateHouseholdsByVehiclesChartByYear(year: number) {
    this.featureStatus.householdsByVehiclesYear = year;

    const filtered = this.householdsByVehiclesData
      .filter((d) => d.year === year)
      .sort((a, b) => a.vehiclesCount - b.vehiclesCount);

    this.fillHouseholdsByVehiclesChart(filtered);
  }

  public updateTimeOfDepartureToWorkChartByYear(year: number) {
    this.featureStatus.timeOfDepartureToWorkYear = year;

    const filtered = this.timeOfDepartureToWorkData
      .filter((d) => d.year === year)
      .sort((a, b) => a.workers - b.workers);

    this.fillTimeOfDepartureToWorkChart(filtered);
  }

  public updateTravelTimeToWorkChartByYear(year: number) {
    this.featureStatus.travelTimeToWorkYear = year;

    const filtered = this.travelTimeToWorkData
      .filter((d) => d.year === year)
      .sort((a, b) => a.minutesFrom - b.minutesFrom);

    this.fillTravelTimeToWorkChart(filtered);
  }

  public updateWorkCommuteModesChartByYear(year: number) {
    this.featureStatus.workCommuteModesYear = year;

    const filtered = this.workCommuteModesData
      .filter((d) => d.year === year)
      .sort((a, b) => a.workers - b.workers);

    this.fillWorkCommuteModesChart(filtered);
  }

  private fillHouseholdsByVehiclesChart(data: HouseholdsByVehicles[]): void {
    if (!data || data.length === 0) {
      this.featureStatus.containsHouseholdsByVehicles = false;
      return;
    }

    const mappedData = data.map((el) => ({
      value: el.households,
      vehicles: el.vehiclesCount,
    }));

    const values = mappedData.map((el) => el.value);
    const keys = mappedData.map((el) => el.vehicles);
    const hoverTexts = values.map((value) =>
      formatChartRangesAndHoverNumber(value)
    );
    const total = values.reduce((acc, curr) => acc + curr, 0);

    if (values.length === 0 || total === 0) {
      this.featureStatus.containsHouseholdsByVehicles = false;
      return;
    }

    const percentages = getPercents(total, values);

    this.featureStatus.containsHouseholdsByVehicles = true;

    this.householdSizeByVehiclesChart.data = [
      {
        x: keys.map(String),
        y: percentages,
        type: 'bar',
        text: hoverTexts,
        textposition: 'none',
        hoverinfo: 'text',
        customdata: percentages.map(
          (value, i) =>
            `${keys[i]} Vehicles: ${value >= 1 ? value.toFixed(1) : '<1'}%`
        ),
        hovertemplate: `%{customdata}<extra></extra>`,
        marker: {
          color: keys.map((value) => {
            switch (value) {
              case 0:
                return '#AD95C6';
              case 1:
                return '#A084BD';
              case 2:
                return '#9272B3';
              case 3:
                return '#8561AA';
              case 4:
                return '#774FA0';
              default:
                return '#CCCCCC';
            }
          }),
        },
      },
    ] as Data;

    this.householdSizeByVehiclesChart.layout = {
      autosize: true,
      xaxis: {
        title: 'Number of Vehicles',
        type: 'category',
        tickfont: { size: 14 },
      },
      yaxis: {
        title: 'Households',
        tickformat: ',.0f',
        ticksuffix: '%',
      },
      bargap: 0.5,
      font: {
        family: 'Rising Sun, sans-serif',
      },
      margin: { t: 35, b: 180, r: 30 },
    } as Layout;
  }

  private fillTimeOfDepartureToWorkChart(data: TimeOfDepartureToWork[]): void {
    if (!data || data.length === 0) {
      this.featureStatus.containsTimeOfDepartureToWork = false;
      return;
    }

    const year = this.featureStatus.timeOfDepartureToWorkYear;
    const filtered = data.filter((d) => d.year === year);
    if (filtered.length === 0) {
      this.featureStatus.containsTimeOfDepartureToWork = false;
      return;
    }

    const parseTime = (timeStr: string): number => {
      const [_, hourStr, minuteStr, meridian] = timeStr.match(
        /(\d+):(\d+)\s*(AM|PM)/
      )!;
      let hour = parseInt(hourStr);
      const minute = parseInt(minuteStr);
      if (meridian === 'PM' && hour !== 12) hour += 12;
      if (meridian === 'AM' && hour === 12) hour = 0;
      return hour * 60 + minute;
    };

    const formatTimeLabel = (timeStr: string): string => {
      const [_, hourStr, minuteStr, meridian] = timeStr.match(
        /(\d+):(\d+)\s*(AM|PM)/
      )!;
      const hour = parseInt(hourStr);
      const minute = parseInt(minuteStr);
      if (minute === 0) {
        return `${hour}${meridian}`;
      } else {
        return `${hour}:${minute.toString().padStart(2, '0')}${meridian}`;
      }
    };

    const grouped = new Map<
      string,
      { totalWorkers: number; intervals: string[] }
    >();

    for (const d of filtered) {
      const from = d.timeInterval.split('–')[0].trim();
      const label = formatTimeLabel(from);

      if (!grouped.has(label)) {
        grouped.set(label, { totalWorkers: 0, intervals: [] });
      }

      const entry = grouped.get(label)!;
      entry.totalWorkers += d.workers;
      entry.intervals.push(d.timeInterval);
    }

    const sorted = Array.from(grouped.entries())
      .map(([label, val]) => {
        const minute = parseTime(val.intervals[0].split('–')[0].trim());
        return { label, ...val, minute };
      })
      .sort((a, b) => a.minute - b.minute);

    const yLabels = sorted.map((d) => d.label);
    const workers = sorted.map((d) => d.totalWorkers);
    const intervals = sorted.map((d) => d.intervals.join(', '));
    const total = workers.reduce((a, b) => a + b, 0);

    if (total === 0) {
      this.featureStatus.containsTimeOfDepartureToWork = false;
      return;
    }

    const percentages = getPercents(total, workers);

    this.featureStatus.containsTimeOfDepartureToWork = true;

    this.timeOfDepartureToWorkChart.data = [
      {
        x: percentages,
        y: yLabels,
        type: 'bar',
        orientation: 'h',
        hoverinfo: 'text',
        customdata: percentages.map(
          (p, i) => `${intervals[i]}: ${p >= 1 ? p.toFixed(1) : '<1'}%`
        ),
        hovertemplate: `%{customdata}<extra></extra>`,
        marker: { color: '#02A4FF' },
      },
    ] as Data;

    this.timeOfDepartureToWorkChart.layout = {
      autosize: true,
      xaxis: {
        title: {
          text: 'Workers, %',
          font: { size: 14 },
        } as any,
        tickformat: ',.0f',
        ticksuffix: '%',
      },
      yaxis: {
        title: '',
        type: 'category',
        showgrid: true,
      },
      margin: { t: 35, b: 180, r: 30, pad: 5 },
      bargap: 0.5,
      font: {
        family: 'Rising Sun, sans-serif',
      },
      hovermode: 'closest',
    } as Layout;
  }

  private fillTravelTimeToWorkChart(data: TravelTimeToWork[]): void {
    if (!data || data.length === 0) {
      this.featureStatus.containsTravelTimeToWork = false;
      return;
    }

    const latestYear = this.featureStatus.travelTimeToWorkYear;
    const filtered = data
      .filter((d) => d.year === latestYear)
      .sort((a, b) => a.minutesFrom - b.minutesFrom);

    if (filtered.length === 0) {
      this.featureStatus.containsTravelTimeToWork = false;
      return;
    }

    const keys = filtered.map((el) => {
      if (el.minutesFrom === 90 && el.minutesTo === 0) return '90+';
      return `${el.minutesFrom}-${el.minutesTo}`;
    });

    const values = filtered.map((el) => el.workers);
    const total = values.reduce((acc, curr) => acc + curr, 0);

    if (values.length === 0 || total === 0) {
      this.featureStatus.containsTravelTimeToWork = false;
      return;
    }

    const percentages = getPercents(total, values);
    const hoverTexts = values.map(
      (value, i) => `${keys[i]} min: ${formatChartRangesAndHoverNumber(value)}`
    );

    this.featureStatus.containsTravelTimeToWork = true;

    this.travelTimeToWorkChart.data = [
      {
        y: keys,
        x: percentages,
        type: 'bar',
        orientation: 'h',
        text: hoverTexts,
        textposition: 'none',
        hoverinfo: 'text',
        customdata: percentages.map(
          (value, i) =>
            `${keys[i]} min: ${value >= 1 ? value.toFixed(1) : '<1'}%`
        ),
        hovertemplate: `%{customdata}<extra></extra>`,
        marker: {
          color: '#774FA0',
        },
      },
    ] as Data;

    this.travelTimeToWorkChart.layout = {
      autosize: true,
      xaxis: {
        title: {
          text: 'Workers, %',
          font: { size: 14 },
        } as any,
        tickformat: ',.0f',
        ticksuffix: '%',
      },
      yaxis: {
        title: 'Travel time to work, min',
      },
      margin: { t: 35, b: 180, r: 30, pad: 5 },
      bargap: 0.5,
      font: {
        family: 'Rising Sun, sans-serif',
      },
    } as Layout;
  }

  private fillWorkCommuteModesChart(data: WorkCommuteModes[]): void {
    if (!data || data.length === 0) {
      this.featureStatus.containsWorkCommuteModes = false;
      return;
    }

    const latestYear = this.featureStatus.workCommuteModesYear;

    const filtered = data.filter((d) => d.year === latestYear);

    if (filtered.length === 0) {
      this.featureStatus.containsWorkCommuteModes = false;
      return;
    }

    const mappedData = filtered
      .map((el) => ({
        value: Math.round(el.workers),
        travelType: this.mapTravelTypeReadable(el.travelType),
      }))
      .filter((el) => el.value > 0);

    const values = mappedData.map((el) => el.value);
    const labels = mappedData.map((el) => el.travelType);

    const total = values.reduce((acc, curr) => acc + curr, 0);

    if (total === 0) {
      this.featureStatus.containsWorkCommuteModes = false;
      return;
    }

    this.featureStatus.containsWorkCommuteModes = true;

    this.workCommuteModesChart.data = [
      {
        values: values,
        labels: labels,
        type: 'pie',
        textposition: 'inside',
        hoverinfo: 'label+percent+value',
        hovertemplate:
          '&nbsp;&nbsp;%{label}: %{value} workers (%{percent})&nbsp;&nbsp;<extra></extra>',
        marker: {
          colors: labels.map((label) => this.getTravelTypeColor(label)),
        },
      },
    ] as Data;

    this.workCommuteModesChart.layout = getPieChartLayout(
      undefined,
      'Travel type',
      'Workers'
    );
  }

  private mapTravelTypeReadable(travelType: string): string {
    switch (travelType) {
      case 'CAR':
        return 'Car';
      case 'PUBLIC':
      case 'PUBLIC_TRANSPORTATION':
        return 'Public transport';
      case 'TAXICAB':
        return 'Taxi';
      case 'MOTORCYCLE':
        return 'Motorcycle';
      case 'BICYCLE':
        return 'Bicycle';
      case 'WALKED':
        return 'Walked';
      case 'OTHER':
      case 'OTHER_MEANS':
        return 'Other';
      case 'WORKED_AT_HOME':
        return 'Worked at home';
      default:
        return 'Other';
    }
  }

  private getTravelTypeColor(travelType: string): string {
    switch (travelType) {
      case 'Car':
        return '#336DB1';
      case 'Public transport':
        return '#F38F20';
      case 'Taxi':
        return '#47A34B';
      case 'Motorcycle':
        return '#D94134';
      case 'Bicycle':
        return '#975BA5';
      case 'Walked':
        return '#855B4A';
      case 'Other':
        return '#D49AAE';
      case 'Worked at home':
        return '#AFAFAF';
      default:
        return '#CCCCCC';
    }
  }

  private fillRoadDensityChart(feature: MapboxGeoJSONFeature): void {
    const areaMi2: number = convertArea(area(feature), 'meters', 'miles');
    const featureId: NaturalId = feature.properties!.external_id;

    this.http.getRoadDensityData(featureId).subscribe(
      (data: JsonResponse<RoadLengthBackend>) => {
        if (data.object.roadLengthLanesWeighted) {
          this.featureStatus.naturalId = featureId;
          this.featureStatus.isSelected = true;
        }

        const maxValue = Math.max(
          ...Object.values(data.object.roadLengthLanesWeighted)
        );

        if (!maxValue || maxValue === -Infinity) {
          this.featureStatus.naturalId = featureId;
          this.featureStatus.containsRoadDensity = false;
        } else {
          this.featureStatus.naturalId = featureId;
          this.featureStatus.containsRoadDensity = true;
        }

        const groupedByFRC = CommuteAndMobilityService.getRoadsGroupedByFRC(
          data.object.roadLengthLanesWeighted
        );

        const values = Object.values(groupedByFRC).map((el) =>
          this.getRoadDensityPerMi2(el, areaMi2).toFixed(2)
        );
        const labels = Object.keys(groupedByFRC).map(
          (el) => el.charAt(0).toUpperCase() + el.slice(1)
        );

        this.roadDensityChart.data = [
          {
            values: values,
            labels: labels,
            type: 'pie',
            textposition: 'inside',
            hoverinfo: 'label+percent+value',
            hovertemplate:
              '&nbsp;&nbsp;%{label}: %{value} mi per mi² (%{percent})&nbsp;&nbsp;<extra></extra>',
          },
        ] as Data;

        this.roadDensityChart.layout = getPieChartLayout(
          undefined,
          'Road type',
          'mi per mi²'
        );
      },
      (error) => {
        this.featureStatus.isSelected = true;
        this.featureStatus.containsRoadDensity = false;
        console.error('ERROR FETCHING ROAD DENSITY', error);
        return of(null);
      }
    );
  }

  public static getRoadsGroupedByFRC(roadLengthData: RoadLength): {
    [key: string]: number;
  } {
    const roadLengthByFrc: { [key: string]: number } = {};

    for (let [frc, length] of Object.entries(roadLengthData)) {
      const frcKey = `FRC ${getFRCLabels(frc)}`;

      if (roadLengthByFrc[frcKey]) {
        roadLengthByFrc[frcKey] += length;
      } else {
        roadLengthByFrc[frcKey] = length;
      }
    }

    return roadLengthByFrc;
  }

  private getRoadDensityPerMi2(
    roadLength: number,
    featureAreaMi2: number
  ): number {
    const metersInMile = 1609.344;
    const roadLengthMi = roadLength / metersInMile;
    const roadDensityPerMi2 = roadLengthMi / featureAreaMi2;

    return roadDensityPerMi2;
  }

  private fillTrafficIntensityChart(feature: MapboxGeoJSONFeature): void {
    const id = feature.properties?.external_id;

    this.http.getTrafficIntensityChartData(id).subscribe(
      (data) => {
        if (data.object.distribution) {
          this.featureStatus.naturalId = id;
          this.featureStatus.isSelected = true;
          this.featureStatus.containsTrafficIntensity = true;
        }

        const groupedByFRC = CommuteAndMobilityService.getRoadsGroupedByFRC(
          data.object.distribution
        );

        const values = Object.values(groupedByFRC).map((el) => Math.round(el));
        const labels = Object.keys(groupedByFRC).map(
          (el) => el.charAt(0).toUpperCase() + el.slice(1)
        );

        const maxValue = Math.max(...values);

        if (!maxValue || maxValue === -Infinity) {
          this.featureStatus.naturalId = id;
          this.featureStatus.containsTrafficIntensity = false;
        } else {
          this.featureStatus.naturalId = id;
          this.featureStatus.containsTrafficIntensity = true;
        }

        this.trafficIntensityChart.data = [
          {
            values: values,
            labels: labels,
            type: 'pie',
            hole: 0.3,
            textposition: 'inside',
            hoverinfo: 'label+percent+value',
            hovertemplate:
              '&nbsp;&nbsp;%{label} AADT: %{value} (%{percent})&nbsp;&nbsp;<extra></extra>',
          },
        ] as Data;

        this.trafficIntensityChart.layout = getPieChartLayout(
          values,
          'Road type',
          'AADT'
        );
      },
      (error) => {
        this.featureStatus.isSelected = true;
        this.featureStatus.containsTrafficIntensity = false;
        console.error('ERROR FETCHING TRAFFIC INTENSITY', error);
        return of(null);
      }
    );
  }
}
